import { useEffect, useMemo, useState } from 'react';

import { Card, IconButton, NumberField, PropRow, SelectField, TextField, Toggle } from '../ui/controls.js';
import { EffectsEditor } from './EffectsEditor.js';
import { SourceEditor } from './LayerInspector.js';
import { docVariables, ParamField } from './ParamField.js';
import { PathField, SchemaFields, withField } from './SchemaFields.js';
import { Progress } from './VoiceEditor.js';
import { isNum, isObj, midiToNote } from './param-values.js';
import {
  DEFAULT_INSTRUMENTS,
  DRUM_PAD_KEYS,
  EFFECT_FIELDS,
  GM_DRUMS,
  GM_FAMILIES,
  GM_PROGRAMS,
  INSTRUMENT_TYPES,
  SINGER_VOICES,
  type InstrumentType,
} from './sound-catalog.js';
import { formatBytes, previewDoc, previewDocument, stopPreview, useSoundCache } from './sound-api.js';
import './sound-editors.css';

type Obj = Record<string, unknown>;

/** GM program number for a `program` value (number or name, case-insensitive); null if unknown. */
export function programNumber(p: unknown): number | null {
  if (isNum(p)) return Math.max(0, Math.min(127, Math.round(p)));
  if (typeof p !== 'string') return null;
  const i = GM_PROGRAMS.findIndex((n) => n.toLowerCase() === p.trim().toLowerCase());
  return i >= 0 ? i : null;
}

/** Notes used for an instrument preview: a short arpeggio (or one hit for drum pads). */
function previewNotes(drumKey?: number) {
  if (drumKey !== undefined) return [{ t: 0, d: 0.5, p: drumKey, v: 0.9 }];
  return [
    { t: 0, d: 0.45, p: 'C4', v: 0.8 },
    { t: 0.5, d: 0.45, p: 'E4', v: 0.8 },
    { t: 1, d: 0.45, p: 'G4', v: 0.8 },
    { t: 1.5, d: 1.2, chord: 'C', v: 0.7 },
  ];
}

/* ------------------------------------------------------------------ */
/* SoundFont program picker                                            */

function SoundFontStatus() {
  const entries = useSoundCache((s) => s.entries);
  const load = useSoundCache((s) => s.load);
  const download = useSoundCache((s) => s.download);
  const downloads = useSoundCache((s) => s.downloads);
  useEffect(() => void load(), [load]);
  const fonts = entries.filter((e) => e.kind === 'soundfont');
  if (fonts.length === 0) return null;
  const installed = fonts.find((f) => f.installed);
  if (installed) return <span className="snd-hint is-ok">{`SoundFont: ${installed.name}${installed.license ? ` · ${installed.license}` : ''}`}</span>;
  const def = fonts.find((f) => f.isDefault) ?? fonts[0]!;
  const dl = downloads[`soundfont:${def.id}`];
  return (
    <div className="snd-row">
      <span className="snd-hint is-warn">The General MIDI SoundFont is not downloaded yet.</span>
      {dl && !dl.error ? (
        <Progress value={dl.progress} label="Downloading…" />
      ) : (
        <button type="button" className="snd-btn" onClick={() => void download('soundfont', def.id)}>
          {`Download ${def.name}${def.size ? ` (${formatBytes(def.size)})` : ''}`}
        </button>
      )}
      {dl?.error && <span className="snd-hint is-error">{dl.error}</span>}
    </div>
  );
}

function ProgramPicker({ value, onChange, drums }: { value: unknown; onChange: (p: number) => void; drums: boolean }) {
  const [query, setQuery] = useState('');
  const current = programNumber(value);
  const needle = query.trim().toLowerCase();
  const groups = useMemo(
    () =>
      GM_FAMILIES.map((family, f) => ({
        family,
        programs: GM_PROGRAMS.slice(f * 8, f * 8 + 8)
          .map((name, i) => ({ name, n: f * 8 + i }))
          .filter((p) => !needle || p.name.toLowerCase().includes(needle) || family.toLowerCase().includes(needle) || String(p.n + 1) === needle),
      })).filter((g) => g.programs.length > 0),
    [needle],
  );
  if (drums) return <span className="snd-hint">Bank 128: General MIDI drum kit — notes map to drum keys (36 kick, 38 snare, 42 hi-hat…).</span>;
  return (
    <div className="snd-programs">
      <input className="textfield" value={query} placeholder="Search 128 GM programs…" onChange={(e) => setQuery(e.target.value)} />
      <div className="snd-programs-list" role="listbox" aria-label="General MIDI programs">
        {groups.map((g) => (
          <div key={g.family} className="snd-programs-group">
            <span className="snd-mini-label">{g.family}</span>
            {g.programs.map((p) => (
              <button
                key={p.n}
                type="button"
                role="option"
                aria-selected={current === p.n}
                className={`snd-program${current === p.n ? ' is-selected' : ''}`}
                onClick={() => onChange(p.n)}
              >
                <span className="snd-program-num mono">{p.n}</span>
                {p.name}
              </button>
            ))}
          </div>
        ))}
        {groups.length === 0 && <span className="snd-hint">No program matches.</span>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Drum kit                                                            */

function DrumPads({ inst, onChange, doc, onPreview }: { inst: Obj; onChange: (i: Obj) => void; doc: unknown; onPreview: (key: number) => void }) {
  const pads: Obj = isObj(inst.pads) ? inst.pads : {};
  const [sel, setSel] = useState<number>(36);
  const [extra, setExtra] = useState<string>('');
  const shown = [...DRUM_PAD_KEYS];
  for (const k of Object.keys(pads)) {
    const n = Number(k);
    if (Number.isFinite(n) && !shown.includes(n)) shown.push(n);
  }
  const pad = pads[String(sel)];
  const padObj: Obj | undefined = isObj(pad) ? pad : undefined;
  const setPad = (v: Obj | undefined) => onChange({ ...inst, pads: withField(pads, String(sel), v) });
  const mode = padObj ? (padObj.sample !== undefined ? 'sample' : 'source') : 'empty';

  return (
    <div className="snd-drums">
      <div className="snd-pad-grid">
        {shown.map((k) => {
          const p = pads[String(k)];
          return (
            <button
              key={k}
              type="button"
              className={`snd-pad${sel === k ? ' is-selected' : ''}${p ? ' is-set' : ''}`}
              title={`${k} · ${GM_DRUMS[k] ?? midiToNote(k)}${p ? '' : ' (empty)'} — double-click to preview`}
              onClick={() => setSel(k)}
              onDoubleClick={() => p && onPreview(k)}
            >
              <span className="snd-pad-key mono">{k}</span>
              <span className="snd-pad-name">{GM_DRUMS[k] ?? midiToNote(k)}</span>
            </button>
          );
        })}
      </div>
      <div className="snd-row">
        <SelectField
          value={extra || undefined}
          placeholder="Other key…"
          options={Array.from({ length: 128 }, (_, n) => ({ value: String(n), label: `${n} · ${GM_DRUMS[n] ?? midiToNote(n)}` }))}
          onChange={(v) => {
            setExtra(v);
            if (v) setSel(Number(v));
          }}
        />
      </div>
      <div className="snd-subcard">
        <div className="snd-row">
          <strong className="snd-mini-label">{`Pad ${sel} · ${GM_DRUMS[sel] ?? midiToNote(sel)}`}</strong>
          <span className="snd-spacer" />
          <SelectField
            value={mode}
            options={[
              { value: 'empty', label: 'Empty' },
              { value: 'source', label: 'Synthesised source' },
              { value: 'sample', label: 'Sample file' },
            ]}
            onChange={(m) => {
              if (m === 'empty') setPad(undefined);
              else if (m === 'sample') setPad({ sample: '' });
              else setPad({ source: { type: 'membrane', size: 0.5, tension: 0.5, damping: 0.3 } });
            }}
          />
          <IconButton icon="play" title="Preview pad" size={13} disabled={!padObj} onClick={() => onPreview(sel)} />
        </div>
        {mode === 'source' && padObj && (
          <>
            <SourceEditor value={padObj.source} doc={doc} depth={1} onChange={(v) => setPad(withField(padObj, 'source', v))} />
            <PropRow label="Amp">
              <ParamField value={padObj.amp} step={0.05} min={0} placeholder={1} vars={docVariables(doc)} onChange={(v) => setPad(withField(padObj, 'amp', v))} />
            </PropRow>
          </>
        )}
        {mode === 'sample' && padObj && (
          <PropRow label="Sample">
            <PathField value={typeof padObj.sample === 'string' ? padObj.sample : undefined} ext={/\.(wav|ogg|mp3|flac)$/i} onChange={(v) => setPad(withField(padObj, 'sample', v ?? ''))} />
          </PropRow>
        )}
        {padObj && (
          <PropRow label="Gain" hint="dB">
            <NumberField value={isNum(padObj.gain) ? padObj.gain : undefined} step={0.5} onChange={(v) => setPad(withField(padObj, 'gain', v))} />
          </PropRow>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sampler                                                             */

function pair(v: unknown): [number | undefined, number | undefined] {
  return Array.isArray(v) ? [isNum(v[0]) ? v[0] : undefined, isNum(v[1]) ? v[1] : undefined] : [undefined, undefined];
}

function SamplerZones({ inst, onChange }: { inst: Obj; onChange: (i: Obj) => void }) {
  const zones: Obj[] = Array.isArray(inst.zones) ? inst.zones.map((z) => (isObj(z) ? z : {})) : [];
  const set = (i: number, k: string, v: unknown) => onChange({ ...inst, zones: zones.map((z, j) => (j === i ? withField(z, k, v) : z)) });
  const setPair = (i: number, k: string, idx: 0 | 1, v: number, dflt: [number, number]) => {
    const [a, b] = pair(zones[i]?.[k]);
    const next: [number, number] = [a ?? dflt[0], b ?? dflt[1]];
    next[idx] = v;
    set(i, k, next);
  };
  return (
    <div className="snd-list">
      {zones.map((z, i) => {
        const vel = pair(z.velocity);
        const loop = pair(z.loop);
        return (
          <div key={i} className="snd-subcard">
            <div className="snd-row">
              <PathField value={typeof z.sample === 'string' ? z.sample : undefined} ext={/\.(wav|ogg|mp3|flac)$/i} placeholder="samples/vln_C4.wav" onChange={(v) => set(i, 'sample', v ?? '')} />
              <IconButton icon="x" title="Remove zone" size={12} onClick={() => onChange({ ...inst, zones: zones.filter((_, j) => j !== i) })} />
            </div>
            <div className="snd-grid4">
              <NumberField label="Root" title={`Root key${isNum(z.root) ? ` (${midiToNote(z.root)})` : ''}`} value={isNum(z.root) ? z.root : undefined} integer min={0} max={127} onChange={(v) => set(i, 'root', v)} />
              <NumberField label="Lo" title="Lowest key" value={isNum(z.low) ? z.low : undefined} integer min={0} max={127} onChange={(v) => set(i, 'low', v)} />
              <NumberField label="Hi" title="Highest key" value={isNum(z.high) ? z.high : undefined} integer min={0} max={127} onChange={(v) => set(i, 'high', v)} />
              <NumberField label="Grp" title="Round-robin group" value={isNum(z.group) ? z.group : undefined} integer min={0} onChange={(v) => set(i, 'group', v)} />
              <NumberField label="Vel≥" title="Velocity range low (0..127)" value={vel[0]} integer min={0} max={127} onChange={(v) => setPair(i, 'velocity', 0, v, [0, 127])} />
              <NumberField label="Vel≤" title="Velocity range high (0..127)" value={vel[1]} integer min={0} max={127} onChange={(v) => setPair(i, 'velocity', 1, v, [0, 127])} />
              <NumberField label="Loop" title="Loop start (samples)" value={loop[0]} integer min={0} onChange={(v) => setPair(i, 'loop', 0, v, [0, 0])} />
              <NumberField label="End" title="Loop end (samples)" value={loop[1]} integer min={0} onChange={(v) => setPair(i, 'loop', 1, v, [0, 0])} />
            </div>
          </div>
        );
      })}
      <div className="snd-row">
        <IconButton
          icon="plus"
          title="Add zone"
          size={13}
          onClick={() => {
            const last = zones[zones.length - 1];
            const low = last && isNum(last.high) ? last.high + 1 : 0;
            onChange({ ...inst, zones: [...zones, { sample: '', root: Math.min(127, low + 4), low, high: Math.min(127, low + 11) }] });
          }}
        />
        <span className="snd-hint">{`${zones.length} zones`}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Editor                                                              */

/**
 * Editor for one instrument definition (`doc.instruments[name]` or a
 * document's `instrument`): SoundFont program picker (GM list + search),
 * synth patch, drum kit pad grid, sampler zones, singer and ref.
 */
export function InstrumentEditor({ value, onChange, doc }: { value: unknown; onChange: (value: unknown) => void; doc?: unknown }) {
  const inst: Obj = isObj(value) ? value : {};
  const type = typeof inst.type === 'string' ? inst.type : '';
  const vars = docVariables(doc);
  const set = (k: string, v: unknown) => onChange(withField(inst, k, v));
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = async (drumKey?: number) => {
    if (playing) {
      stopPreview();
      setPlaying(false);
      return;
    }
    setPlaying(true);
    setError(null);
    try {
      const base = previewDocument('Instrument preview', {}, doc);
      const instruments = { ...(isObj(base.instruments) ? base.instruments : {}), __preview: inst };
      await previewDoc({
        ...base,
        kind: 'sfx',
        instruments,
        layers: [{ name: 'preview', source: { type: 'instrument', instrument: '__preview', notes: previewNotes(drumKey) } }],
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPlaying(false);
    }
  };

  const drums = inst.bank === 128;

  return (
    <div className="snd-editor">
      <div className="snd-row">
        <SelectField
          value={type || undefined}
          placeholder="Instrument type…"
          options={INSTRUMENT_TYPES}
          onChange={(t) => onChange(structuredClone(DEFAULT_INSTRUMENTS[t as InstrumentType]))}
        />
        <IconButton icon={playing ? 'stop' : 'play'} title={playing ? 'Stop' : 'Preview (C major arpeggio)'} size={14} onClick={() => void preview()} />
      </div>
      {error && <span className="snd-hint is-error">{error}</span>}

      {type === 'soundfont' && (
        <>
          <SoundFontStatus />
          <PropRow label="Drums" hint="Bank 128 = GM percussion">
            <Toggle checked={drums} onChange={(d) => onChange({ ...inst, bank: d ? 128 : 0, program: 0 })} />
          </PropRow>
          <PropRow label="Bank">
            <NumberField value={isNum(inst.bank) ? inst.bank : 0} integer min={0} max={128} onChange={(v) => set('bank', v)} />
          </PropRow>
          <PropRow label="Program">
            <span className="snd-chip">{(() => {
              const n = programNumber(inst.program);
              return n === null ? String(inst.program ?? '—') : `${n} · ${GM_PROGRAMS[n]}`;
            })()}</span>
          </PropRow>
          <ProgramPicker value={inst.program} drums={drums} onChange={(p) => set('program', p)} />
          <PropRow label="SoundFont" hint="Optional .sf2 in the project or cache (default: shared GM SoundFont)">
            <PathField value={typeof inst.soundfont === 'string' ? inst.soundfont : undefined} ext={/\.sf2$/i} placeholder="default GM SoundFont" onChange={(v) => set('soundfont', v)} />
          </PropRow>
        </>
      )}

      {type === 'synth' && (
        <>
          <div className="snd-grid4">
            <NumberField label="Voices" title="Polyphony" value={isNum(inst.voices) ? inst.voices : 8} integer min={1} max={64} onChange={(v) => set('voices', v)} />
            <NumberField label="Glide" title="Portamento (s)" value={isNum(inst.glide) ? inst.glide : undefined} step={0.01} min={0} onChange={(v) => set('glide', v)} />
            <NumberField label="Unison" title="Unison voices per note" value={isNum(inst.unison) ? inst.unison : isObj(inst.unison) && isNum(inst.unison.voices) ? inst.unison.voices : undefined} integer min={1} max={16} onChange={(v) => set('unison', v)} />
            <Toggle checked={inst.legato === true} label="Legato" onChange={(l) => set('legato', l ? true : undefined)} />
          </div>
          <Card title="Oscillator / source">
            <SourceEditor value={inst.source} doc={doc} depth={1} onChange={(v) => set('source', v)} />
          </Card>
          <Card title="Amp envelope">
            <ParamField value={inst.amp} step={0.05} min={0} placeholder={1} vars={vars} kinds={['adsr', 'env', 'number', 'lfo']} onChange={(v) => set('amp', v)} />
          </Card>
          <Card title="Filter" defaultOpen={isObj(inst.filter)}>
            {isObj(inst.filter) ? (
              <>
                <SchemaFields
                  obj={inst.filter}
                  fields={[...EFFECT_FIELDS.filter, { key: 'env', kind: 'param', step: 0.05, hint: 'Cutoff envelope amount (octaves), e.g. ADSR' }]}
                  vars={vars}
                  skip={['type']}
                  onChange={(f) => set('filter', f)}
                />
                <button type="button" className="snd-link" onClick={() => set('filter', undefined)}>
                  remove filter
                </button>
              </>
            ) : (
              <button type="button" className="snd-btn" onClick={() => set('filter', { mode: 'lowpass', cutoff: 2000, resonance: 0.2 })}>
                Add filter
              </button>
            )}
          </Card>
          <Card title="Effects" defaultOpen={Array.isArray(inst.effects) && inst.effects.length > 0}>
            <EffectsEditor value={inst.effects} doc={doc} onChange={(v) => set('effects', v)} />
          </Card>
        </>
      )}

      {type === 'drumkit' && <DrumPads inst={inst} doc={doc} onChange={onChange} onPreview={(k) => void preview(k)} />}

      {type === 'sampler' && (
        <>
          <SamplerZones inst={inst} onChange={onChange} />
          <PropRow label="Amp">
            <ParamField value={inst.amp} step={0.05} min={0} placeholder={1} vars={vars} kinds={['adsr', 'env', 'number']} onChange={(v) => set('amp', v)} />
          </PropRow>
        </>
      )}

      {type === 'singer' && (
        <>
          <PropRow label="Voice">
            <SelectField value={typeof inst.voice === 'string' ? inst.voice : undefined} placeholder="soprano" options={SINGER_VOICES} onChange={(v) => set('voice', v)} />
          </PropRow>
          <PropRow label="Vibrato">
            <ParamField value={inst.vibrato} step={0.05} min={0} placeholder={0.3} vars={vars} onChange={(v) => set('vibrato', v)} />
          </PropRow>
          <PropRow label="Breath">
            <ParamField value={inst.breath} step={0.05} min={0} max={1} placeholder={0.2} vars={vars} onChange={(v) => set('breath', v)} />
          </PropRow>
          <PropRow label="Tension">
            <ParamField value={inst.tension} step={0.05} min={0} max={1} placeholder={0.5} vars={vars} onChange={(v) => set('tension', v)} />
          </PropRow>
        </>
      )}

      {type === 'ref' && (
        <PropRow label="Document" hint="Uses another sound document's `instrument`">
          <PathField value={typeof inst.path === 'string' ? inst.path : undefined} ext={/\.sound\.json$/i} placeholder="sounds/lead.sound.json" onChange={(v) => set('path', v ?? '')} />
        </PropRow>
      )}

      {type !== '' && type !== 'ref' && type !== 'soundfont' && type !== 'synth' && (
        <PropRow label="Gain" hint="dB">
          <NumberField value={isNum(inst.gain) ? inst.gain : undefined} step={0.5} onChange={(v) => set('gain', v)} />
        </PropRow>
      )}
      {type === '' && <span className="snd-hint">Pick an instrument type.</span>}
      {type !== '' && !(INSTRUMENT_TYPES as readonly string[]).includes(type) && (
        <PropRow label="Name">
          <TextField value={type} onChange={(t) => set('type', t)} />
        </PropRow>
      )}
    </div>
  );
}
